"use client";

import { useEffect } from "react";
import { toast } from "sonner";
import { captureDiagnosticBundle } from "@/utils/diagnostics-buffer";

/**
 * Keyboard-only diagnostic capture.
 *
 * Press Alt+Shift+C at the moment something feels slow/laggy/wrong. It flushes
 * the wide rolling buffer (the last ~10 min of console, network timing, long
 * tasks, laggy interactions, web-vitals) plus point-in-time snapshots as a
 * single "manual_capture" event the agent can read back in full. A toast
 * confirms it fired and shows the tag.
 *
 * No visible button by design — invisible until the shortcut is pressed.
 */
export function DiagnosticsCaptureHotkey() {
  useEffect(() => {
    let busy = false;

    const onKeyDown = async (e: KeyboardEvent) => {
      // Alt+Shift+C — uses e.code (physical key) so it works regardless of the
      // character Alt+Shift produces on Mac/Windows.
      if (!(e.altKey && e.shiftKey && e.code === "KeyC")) return;
      e.preventDefault();
      if (busy) return;
      busy = true;

      const id = toast.loading("Capturing diagnostics…");
      try {
        const result = await captureDiagnosticBundle();
        if (result) {
          toast.success("Diagnostics captured", {
            id,
            description: `Tag: ${result.tag} — tell the agent to pull this capture.`,
            duration: 6000,
          });
        } else {
          toast.error("Capture unavailable on this page", { id });
        }
      } catch {
        toast.error("Capture failed", { id });
      } finally {
        busy = false;
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return null;
}
